/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { CSSProperties, ReactNode } from 'react';

import { css as cssUnbounded, jsx, keyframes } from '@compiled/react';

import { token } from '@atlaskit/tokens';

const DEFAULT_MAX_VISIBLE = 2;
const DEFAULT_AMPLITUDE = 4;
const DEFAULT_BOB_DURATION = 620;
const DEFAULT_STAGGER = 180;
const DEFAULT_WAVE_COUNT = 3;
const DEFAULT_BETWEEN_WAVES_PAUSE = 300;
const DEFAULT_INITIAL_DELAY = 460;
const DEFAULT_WITH_GHOST_FRAME = true;
const GHOST_FRAME_MASK_BORDER_PX = 1;
const AMPLITUDE_CSS_VAR = '--gw-amp';
const DELAY_CSS_VAR = '--gw-delay';
const DURATION_CSS_VAR = '--gw-duration';
const EASING_CSS_VAR = '--gw-easing';
const ITERATIONS_CSS_VAR = '--gw-iterations';
const ICON_OPACITY_CSS_VAR = '--gw-icon-opacity';
const DEFAULT_ICON_OPACITY = 0.7;
const INFINITE_ITERATION_COUNT = 'infinite';
const ROLE_IMG = 'img';
const SLOT_DATA_ATTR = 'data-ghost-icon-wave-slot';
const FRAME_DATA_ATTR = 'data-ghost-icon-wave-frame';

export type GhostIconWaveProps = {
	/** Icons to animate, in order (left to right). */
	icons: ReactNode[];
	/** How many of those icons actually render. Extras are ignored. */
	maxVisible?: number;
	/** How far up each icon bobs, in pixels. */
	amplitude?: number;
	/** How long one bob (up + down) takes, in ms. */
	bobDuration?: number;
	/**
	 * Delay between adjacent icons starting their bob, in ms. This is what
	 * makes the wave travel.
	 */
	stagger?: number;
	/**
	 * How many full sweeps before the wave stops.
	 * Pass `0` to render icons at rest with no animation.
	 * Pass `Infinity` to loop forever.
	 */
	waveCount?: number;
	/** How long the row rests between sweeps, in ms. */
	betweenWavesPause?: number;
	/** How long to wait before the first icon starts, in ms. */
	initialDelay?: number;
	/** Whether to draw the dashed outline around each icon. */
	withGhostFrame?: boolean;
	/**
	 * Opacity applied to each icon's content (0–1). When omitted, the prop has no
	 * JS default and the CSS `var()` fallback of `0.7` governs. Only has an
	 * effect when `withGhostFrame` is true, since the ghost styling is what fades
	 * the icon.
	 */
	iconOpacity?: number;
	/**
	 * Optional accessible name. When set, the row is announced as an image
	 * (role="img" + aria-label); when omitted, it is treated as decorative.
	 *
	 * Consumers must pass a pre-translated string (typically via
	 * `formatMessage`) since this value is rendered as `aria-label` and read
	 * aloud by screen readers.
	 */
	label?: string;
};

// Two keyframes only (rest -> peak). This keeps the animation as a single
// segment so the timing function drives the entire rise/fall/rest shape
// itself, rather than CSS applying it independently to each keyframe segment
// (which would otherwise cause the icon to "hang" at the peak and at rest
// before each segment's next phase began).
const bobKeyframes = keyframes({
	'0%': { transform: 'translateY(0)' },
	'100%': {
		transform: `translateY(calc(-1 * var(${AMPLITUDE_CSS_VAR}, 0px)))`,
	},
});

const rowStyles = cssUnbounded({
	display: 'inline-flex',
	alignItems: 'center',
	gap: token('space.050'),
});

const slotStyles = cssUnbounded({
	display: 'inline-grid',
	placeItems: 'center',
	animationName: bobKeyframes,
	animationDelay: `var(${DELAY_CSS_VAR}, 0ms)`,
	animationDuration: `var(${DURATION_CSS_VAR}, 0ms)`,
	animationTimingFunction: `var(${EASING_CSS_VAR}, linear)`,
	animationIterationCount: `var(${ITERATIONS_CSS_VAR}, 0)`,
	animationFillMode: 'none',
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
		transform: 'none',
	},
});

const ghostFrameStyles = cssUnbounded({
	position: 'absolute',
	inset: 0,
	boxSizing: 'border-box',
	borderWidth: token('border.width'),
	borderStyle: 'dashed',
	borderColor: token('color.border'),
	borderRadius: token('radius.small'),
	pointerEvents: 'none',
	'@media (prefers-color-scheme: dark)': {
		borderColor: token('color.border.bold'),
	},
});

const ghostFrameSurfaceMaskStyles = cssUnbounded({
	position: 'absolute',
	inset: 0,
	boxSizing: 'border-box',
	borderWidth: `${GHOST_FRAME_MASK_BORDER_PX}px`,
	borderStyle: 'solid',
	borderColor: token('elevation.surface'),
	borderRadius: token('radius.small'),
	pointerEvents: 'none',
});

const ghostFrameWrapperStyles = cssUnbounded({
	position: 'relative',
	display: 'inline-grid',
	placeItems: 'center',
});

const ghostIconContentStyles = cssUnbounded({
	display: 'inline-grid',
	placeItems: 'center',
	// The icon opacity is driven by a CSS custom property so consumers can
	// override it per-render (e.g. behind an experiment) without forking the
	// rest of the filter. Falls back to the default when the var is unset.
	filter: `saturate(0.4) contrast(0.9) brightness(1.12) opacity(var(${ICON_OPACITY_CSS_VAR}, ${DEFAULT_ICON_OPACITY}))`,
});

/**
 * Renders a short row of icons with a staggered left-to-right bob animation.
 *
 * The animation is fully CSS-driven: each icon receives a per-slot
 * `animationDelay` and `animationDuration` so the wave repeats `waveCount`
 * times without any JS timers, making the component SSR-safe and friendly to
 * the React Compiler. Consumers can replay the wave by changing the React
 * `key` prop on the component. Passing `waveCount={0}` renders icons in
 * ghost frames at rest (no motion), which is useful when callers only want to
 * animate first-view states and keep subsequent renders static.
 */
const GhostIconWave = ({
	icons,
	maxVisible = DEFAULT_MAX_VISIBLE,
	amplitude = DEFAULT_AMPLITUDE,
	bobDuration = DEFAULT_BOB_DURATION,
	stagger = DEFAULT_STAGGER,
	waveCount = DEFAULT_WAVE_COUNT,
	betweenWavesPause = DEFAULT_BETWEEN_WAVES_PAUSE,
	initialDelay = DEFAULT_INITIAL_DELAY,
	withGhostFrame = DEFAULT_WITH_GHOST_FRAME,
	iconOpacity,
	label,
}: GhostIconWaveProps): JSX.Element | null => {
	const visible = icons.slice(0, maxVisible);
	// One full sweep = time from the first icon starting to the last icon
	// finishing its bob, plus a quiet pause between sweeps. Every slot uses
	// the same cycle length so their phases stay aligned across iterations.
	const sweepSpan = Math.max(0, visible.length - 1) * stagger;
	const sweepCycleMs = sweepSpan + bobDuration + betweenWavesPause;
	const iterationCount = waveCount === Infinity ? INFINITE_ITERATION_COUNT : waveCount;

	// The keyframes are just `rest -> peak`, so a single segment spans the
	// whole animation. The timing function drives keyframe progress through
	// the full rise/fall/rest shape: up to `1` (peak) at the bob's midpoint,
	// back to `0` (rest) at the bob's end, then held at `0` for the remainder
	// of the cycle. The peak is just an instant — the icon never hangs at the
	// top and comes back down as soon as it peaks.
	const bobFraction = sweepCycleMs > 0 ? Math.min(1, bobDuration / sweepCycleMs) : 1;
	const peakPct = (bobFraction / 2) * 100;
	const bobEndPct = bobFraction * 100;
	const scrunchedEasing = `linear(0, 1 ${peakPct.toFixed(2)}%, 0 ${bobEndPct.toFixed(2)}%, 0)`;

	return (
		// `role="img"` is NOT a leaf role per ARIA — its subtree is not auto-
		// hidden, so we must explicitly hide the slot children below. When
		// `label` is omitted the whole primitive is purely decorative and we
		// hide the outer span outright.
		<span
			css={rowStyles}
			role={label ? ROLE_IMG : undefined}
			aria-label={label}
			aria-hidden={label ? undefined : true}
		>
			{visible.map((icon, index) => {
				const slotChildren = withGhostFrame ? (
					<span css={ghostFrameWrapperStyles}>
						<span
							css={ghostIconContentStyles}
							// Only set the override when a value is provided; otherwise the CSS
							// `var(..., DEFAULT_ICON_OPACITY)` fallback governs.
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- icon opacity is set inline as a CSS custom property so it can be overridden per-render
							style={
								iconOpacity === undefined
									? undefined
									: ({ [ICON_OPACITY_CSS_VAR]: `${iconOpacity}` } as CSSProperties)
							}
						>
							{icon}
						</span>
						<span css={ghostFrameSurfaceMaskStyles} />
						<span css={ghostFrameStyles} {...{ [FRAME_DATA_ATTR]: '' }} />
					</span>
				) : (
					icon
				);

				const slotInlineStyle = {
					[AMPLITUDE_CSS_VAR]: `${amplitude}px`,
					[DELAY_CSS_VAR]: `${initialDelay + index * stagger}ms`,
					[DURATION_CSS_VAR]: `${sweepCycleMs}ms`,
					[EASING_CSS_VAR]: scrunchedEasing,
					[ITERATIONS_CSS_VAR]: `${iterationCount}`,
				} as CSSProperties;

				return (
					<span
						key={index}
						css={slotStyles}
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- per-slot animation timing must be set inline as CSS custom properties
						style={slotInlineStyle}
						aria-hidden={true}
						{...{ [SLOT_DATA_ATTR]: '' }}
					>
						{slotChildren}
					</span>
				);
			})}
		</span>
	);
};

export { GhostIconWave };
